/**
 * Manage lookaheads component (manage-lookaheads.component.ts).
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.37.0 OAUI-532 Add missing filters for v2 orders list
 * 4.36.0 OAUI-517 Work Orders V2: add UI for adding a lookahead order
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WdOrdersService } from '../../../services/orders/wd-orders.service';
import { WdLocalEnvironmentService } from '../../../services/environment/wd-local-environment.service';
import { NgForm } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { WdJobService } from '../../../services/jobs/wd-jobs.service';
import { WdLocalSharedFunctionsService } from '../../../services/shared/wd-local-shared-functions.service';
import { WdManageOrdersService } from '../../../services/orders/manage/wd-manage-orders.service';
import { IOrderLookaheadModel } from '../../../interfaces/orders/order-interfaces';

@Component({
  selector: 'app-manage-lookaheads',
  templateUrl: './manage-lookaheads.component.html',
  styleUrls: ['./manage-lookaheads.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ManageLookaheadsComponent implements OnInit, AfterViewInit {
  // Form
  @ViewChild('lookaheadsForm', { static: true })
  public lookaheadsForm!: NgForm;

  // Inputs
  lookahead: IOrderLookaheadModel;
  lookaheadUntouched: string;
  group: any;
  orderIndex: string;

  // Booleans
  isChanges = false;
  isDatesValid = false;
  isLoading = false;
  isSaving = false;
  searchingPhases = false;

  // Dates
  minDateRequested = new Date();

  // Callbacks
  onSuccess: any;

  // Variables
  phaseTypeaheadResults = new BehaviorSubject<Array<object>>([]);

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    public changeDetector: ChangeDetectorRef,
    public wdJobService: WdJobService,
    public wdManageOrdersService: WdManageOrdersService,
    public wdOrdersService: WdOrdersService,
    public dialogRef: MatDialogRef<ManageLookaheadsComponent>,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    // Defaults
    this.lookahead = this.initializeLookahead(data.lookahead);
    this.group = data.group;

    this.orderIndex = data.orderIndex;

    // Capture Changes
    this.lookaheadUntouched = JSON.stringify(this.lookahead);

    // Functions
    this.onSuccess = data.onSuccessCallback;
  }

  /**
   * Initialize Lookahead
   * @since 4.36.0
   */
  initializeLookahead(lookahead: any) {
    // Set days required by company between order and request
    const minimumDaysRequired = this.wdLocalSharedFunctions.limits
      .minimumProductionDaysRequired;
    this.minDateRequested.setDate(
      this.minDateRequested.getDate() + minimumDaysRequired,
    );

    return {
      index: lookahead ? lookahead.index : null,
      label: lookahead ? lookahead.label : null,
      description: lookahead ? lookahead.description : null,
      orderedFor: lookahead ? lookahead.orderedFor : null,
      job: lookahead ? lookahead.job : null,
      phase: lookahead ? lookahead.phase : null,
      measuredBy: lookahead ? lookahead.measuredBy : null,
      poNumber: lookahead ? lookahead.poNumber : null,
      userGroup: lookahead ? lookahead.userGroup : null,
      dateOrdered: lookahead ? lookahead.dateOrdered : new Date(),
      dateRequested: lookahead
        ? lookahead.dateRequested
        : this.minDateRequested,
      dateProduction: lookahead ? lookahead.dateProduction : null,
      dateShipping: lookahead ? lookahead.dateShipping : null,
    };
  }

  /**
   * On Changes to Inputs
   * @since 4.36.0
   */
  checkForChanges(): void {
    this.isDatesValid =
      !!this.lookahead.dateOrdered &&
      !!this.lookahead.dateRequested &&
      !!this.lookahead.dateProduction &&
      !!this.lookahead.dateShipping;
    this.isChanges = this.lookaheadUntouched !== JSON.stringify(this.lookahead);
  }

  /**
   * Job Model Changes
   * @since 4.36.0
   */
  async jobChange(model: any) {
    // Check for Value Change
    this.checkForChanges();
    // Set Phases
    void this.jobPhasesSearch(model);
  }

  /**
   * Job Phase Search
   * Based on the job provided, find and return all matching phases via API call
   * @since 4.36.0
   */
  async jobPhasesSearch(job?: any) {
    if (job && job.index) {
      // Start Loader
      this.searchingPhases = true;
      // Phases
      let phases: Array<any> = [];
      if (job && job.index) {
        phases = await this.wdJobService.getPhasesByIndex(job.index);
      } else {
        // If no job, clear the current phase
        this.lookahead.phase = null;
      }
      // Set phases
      this.phaseTypeaheadResults.next(phases);
      // Stop Loader
      this.searchingPhases = false;
    } else {
      // Clear phase / phase options
      this.lookahead.phase = null;
      this.phaseTypeaheadResults.next([]);
    }
  }

  /**
   * On Init
   * @since 4.36.0
   */
  async ngOnInit() {
    // Start Loader
    this.isLoading = true;

    // Load Required Data
    await this.wdManageOrdersService.fetchRequiredOrderDetailsData();

    // Set Ordered For User
    const orderedForUserIndex = this.wdEnvironmentService.user?.index;
    this.lookahead.orderedFor = this.wdManageOrdersService.orderDetailsData.users.value.find(
      (user: any) => {
        return user.id === orderedForUserIndex;
      },
    );

    // Stop Loader
    this.isLoading = false;
  }

  /**
   * On Modal Submit
   * @since 4.36.0
   */
  async onSubmit(lookahead: any): Promise<void> {
    // Start Loader
    this.isLoading = true;
    try {
      await this.wdOrdersService.addLookaheadOrder(lookahead);
      // Close the dialog, return false
      this.onSuccess();
      this.dialogRef.close(false);
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * On Modal Dismiss
   * @since 4.36.0
   */
  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(false);
  }

  /**
   * After View Init
   * @since 4.36.0
   */
  ngAfterViewInit() {
    // clear anything at the parent form level
    this.lookaheadsForm?.reset(this.lookahead);
    // Detect Changes
    this.changeDetector.detectChanges();
  }

  /**
   * On Blur Job Typeahead
   * @since 4.37.0
   */
  typeaheadJobBlur(model: any) {
    // Clear value if no model selected
    if (!model?.index) {
      this.lookahead.job = null;
    }
  }
}
